<script lang="ts">
  import { onMount } from 'svelte';
  import CommandBar from './CommandBar.svelte';

  let showActionBar = $state(false);
  let pullDistance = $state(0);
  const PULL_THRESHOLD = 150;

  let startY = 0;
  let currentY = 0;
  let cmdBarInputRef = $state<HTMLInputElement>();

  let exclusionPaths = ['/digital-guide/*'];

  function isOnExclusionPath(): boolean {
    const currentPath = window.location.pathname;
    return exclusionPaths.some((path) => {
      if (path.endsWith('/*')) {
        return currentPath.startsWith(path.slice(0, -2));
      }
      return currentPath === path;
    });
  }

  function onAction() {
    resetActionBar();
  }

  function handlePTRState(disablePTR: boolean) {
    if (disablePTR) {
      document.documentElement.classList.add('disable-ptr');
      document.body.classList.add('disable-ptr');
    } else {
      document.documentElement.classList.remove('disable-ptr');
      document.body.classList.remove('disable-ptr');
    }
  }

  $effect(() => {
    if (showActionBar && cmdBarInputRef) {
      cmdBarInputRef.focus();
    }
  });

  function handleTouchStart(event: TouchEvent) {
    const touch = event.touches[0];
    if (touch) {
      startY = touch.clientY;
      currentY = startY;
    }
  }

  function getScrollTop(): number {
    const container = document.querySelector<HTMLElement>('[data-scroll-container]');
    return container ? container.scrollTop : 0;
  }

  function handleTouchMove(event: TouchEvent) {
    const touch = event.touches[0];
    if (touch) {
      currentY = touch.clientY;
      if (getScrollTop() === 0 && !isOnExclusionPath()) {
        pullDistance = Math.max(0, currentY - startY);
        if (pullDistance >= PULL_THRESHOLD) {
          showActionBar = true;
        }
      }
    }
  }

  function handleTouchEnd() {
    if (!showActionBar) {
      pullDistance = 0;
    } else {
      pullDistance = PULL_THRESHOLD;
    }
    handlePTRState(!showActionBar);
  }

  function resetActionBar() {
    showActionBar = false;
    pullDistance = 0;
    handlePTRState(!showActionBar);
    if (cmdBarInputRef) {
      cmdBarInputRef.value = '';
    }
  }

  function toggleActionBar() {
    if (isOnExclusionPath()) return;
    showActionBar = !showActionBar;
    if (!showActionBar) {
      pullDistance = 0;
    } else {
      pullDistance = PULL_THRESHOLD;
    }
    handlePTRState(!showActionBar);
  }

  function handleOpenEvent() {
    if (isOnExclusionPath()) return;
    pullDistance = PULL_THRESHOLD;
    showActionBar = true;
    handlePTRState(false);
  }

  function handleKeydown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      pullDistance = PULL_THRESHOLD;
      toggleActionBar();
    }
    if (event.key === 'Escape') {
      resetActionBar();
    }
  }

  onMount(() => {
    handlePTRState(!showActionBar);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('open-command-bar', handleOpenEvent);
    // Capture phase to intercept Cmd+K / Ctrl+K before browser handles it
    window.addEventListener('keydown', handleKeydown, { capture: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('open-command-bar', handleOpenEvent);
      window.removeEventListener('keydown', handleKeydown, { capture: true });
    };
  });
</script>

<!-- Overlay -->
{#if showActionBar}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    onclick={resetActionBar}
    class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] transition-opacity duration-300"
  ></div>
{/if}

<!-- Pull-down container -->
<div
  class="fixed left-0 right-0 z-[1000] transition-transform duration-100 ease-out"
  style="top: -60px; transform: translateY({pullDistance}px);"
>
  <div
    class="px-4 md:px-8 py-2 flex justify-center flex-col transition-transform duration-300 ease-out max-w-3xl mx-auto"
    class:translate-y-0={showActionBar}
    class:-translate-y-full={!showActionBar}
  >
    <CommandBar bind:inputRef={cmdBarInputRef} {onAction} />
  </div>
</div>
